import { CONFIG } from "~/config";
import { node, icon } from "~/util/dom";
import { getImageData, IImageData } from "~/places/imageData";
import { _ } from "~/i18n";
import { iframeAnalyticsLog } from "~/util/analyticsAction";
import Scene from "~/scene";
import SceneMarkerMapyWindyPlace from "~/scene/marker/mapy-windy-place";
import SceneMarkerWindy from "~/scene/marker/windy";
import SceneLayerMarker from "~/scene/layer/marker";
import SceneLayerGeometry from "~/scene/layer/geometry";
import SceneCoords from "~/scene/common/coords";
import SceneGeometrySmart from "~/scene/geometry/smart";
import WindyWebcamMarker from "~/scene/marker/windy-webcam";
import { createGeometryCardMarker } from "~/ui/geometryCard";
import { store } from "~/state/store";
import { unsetDetail } from "~/state/detail/state";
import { getIcon } from "~/util/providers/poiagg";
import { setCoordinates } from "~/state/map/state";
import { Unsubscribe } from "redux";
import { watch, watchCoordinates } from "~/state/util";
import { IMapyBoxPlacesFilter } from "~/interfaces";

export interface IMarkerOptions {
	type: string;
	m3dData: any;
	icon?: string;
	srcset?: string;
	elm?: HTMLElement;
	cardClick?: () => void;
}

export const GEOM_RED_COLOR = "#f00";
export const GEOM_GRAY_COLOR = "#6b7580";

function createGeometryLabelMarker(coords: SceneCoords, text: string, id: string): SceneMarkerWindy {
	const label = Scene.createMarkerWindy(coords, `${id}-label-${Date.now()}`, { type: Scene.VISUAL.MARKER_WINDY.LITTLE });

	label.setLabel(text);
	label.setStyle("visibility", "hidden");

	return label;
}

function createSVGIcon(imageData: IImageData): HTMLSpanElement {
	const iconCover = node("span", { className: "mymap-svg-layer" });

	iconCover.appendChild(imageData.svg?.() || icon(imageData.svgName || "star-line"));

	return iconCover;
}

export default class Lookup {
	_owner: any;

	_markerLayer: SceneLayerMarker;

	_geomLayer: SceneLayerGeometry;

	_folderId: string;

	_items: Array<any>;

	_root: boolean;

	_deactiveItems: boolean;

	_highlightItem: any;

	_showLabel: number;

	_activeDetail: any;

	_allMarkers: Array<SceneMarkerMapyWindyPlace | SceneMarkerWindy>;

	_addedMarker: SceneMarkerMapyWindyPlace;

	_hideId: string;

	_openingItem: any;

	_highlightGeometryItem: SceneGeometrySmart;

	_storeUnsubscribe: Unsubscribe;

	_onGetData: () => IMapyBoxPlacesFilter;

	constructor(owner, onGetData: () => IMapyBoxPlacesFilter) {
		this._owner = owner;
		this._markerLayer = Scene.createLayerMarker() as SceneLayerMarker;
		this._geomLayer = Scene.createLayerGeometry("places-item");
		this._folderId = "0";
		this._items = [];
		this._root = false;
		this._deactiveItems = false;
		this._highlightItem = null;
		this._highlightGeometryItem = null;
		this._showLabel = 0;
		this._activeDetail = null;
		this._allMarkers = [];
		this._addedMarker = null;
		this._hideId = "";
		this._openingItem = null;
		this._storeUnsubscribe = null;
		this._onGetData = onGetData;
	}

	destroy() {
		this._markerLayer.destroy();
		this._geomLayer.destroy();

		this._items = [];
		this._allMarkers = [];
		this._hideId = "";

		this._activeDetail = null;
		this._addedMarker = null;
		this._openingItem = null;
		this._highlightGeometryItem = null;
		this._storeUnsubscribe?.();
		this._storeUnsubscribe = null;
	}

	set folderId(folderId: string) {
		this._folderId = folderId;
	}

	set showLabel(show: number) {
		this._showLabel = show;
	}

	start() {
		const wt = watch(store.getState, "map", watchCoordinates);

		Scene.addLayer(this._markerLayer).enable();
		Scene.addLayer(this._geomLayer).enable();
		this._hideId = "";
		this._storeUnsubscribe = store.subscribe(wt((newMap, oldMap) => {
			if (
				newMap.longitude !== oldMap.longitude
				|| newMap.latitude !== oldMap.latitude
				|| newMap.zoom !== oldMap.zoom
			) {
				this.lookup();
			}
		}));
	}

	stop() {
		this._markerLayer.disable();
		Scene.removeLayer(this._markerLayer);
		this._geomLayer.disable();
		Scene.removeLayer(this._geomLayer);
		this._storeUnsubscribe?.();
		this._storeUnsubscribe = null;
	}

	clean() {
		this._markerLayer.removeAll();
		this._geomLayer.removeAll();
		this._highlightGeometryItem = null;
		this._allMarkers = [];
		this._openingItem = null;

		this._highlightItem = null;
	}

	hideMarker(data?: any) {
		if (data) {
			for (let markerIndex = 0; markerIndex < this._allMarkers.length; markerIndex++) {
				if (this._allMarkers[markerIndex].getId().split("-")[0] === data.id) {
					this._markerLayer.removeMarker(this._allMarkers[markerIndex]);
				}
			}

			this._hideId = data.id;
		} else {
			this._markerLayer.removeAll();
			this._markerLayer.addMarker(this._allMarkers);
			this._hideId = "";
		}

		if (this._addedMarker) {
			this._markerLayer.removeMarker(this._addedMarker);
			this._addedMarker = null;
		}
	}

	hideGeometry(data?: any) {
		for (let itemIndex = 0; itemIndex < this._items.length; itemIndex++) {
			const item = this._items[itemIndex];

			if (data && item.id === data.id && item.geometry && item.geometry.appended) {
				this._geomLayer.removeSmartGeometry(item.geometry);
			} else {
				item.geometry && !item.geometry.appended && this._geomLayer.addSmartGeometry(item.geometry);
			}
		}
	}

	disableInteraction() {
		this._deactiveItems = true;

		this._highlightMarkers({});
		this._highlightGeometry(() => false);
	}

	enableInteraction(force?: boolean) {
		if (this._hideId && !force) {
			return;
		}

		this._deactiveItems = false;

		this._highlightMarkers();
		this._highlightGeometry(() => true);
	}

	activeDetail(state: any, data: any) {
		if (state) {
			const findMarker = item => {
				const markers = this._markerLayer.getMarkers();

				for (let markerIndex = 0; markerIndex < markers.length; markerIndex++) {
					if (markers[markerIndex].getId().split("-")[0] === item.id) {
						this._markerLayer.removeMarker(markers[markerIndex]);
						this._activeDetail.marker = markers[markerIndex];
						break;
					}
				}
			};

			this._activeDetail = {
				item: data,
			};

			for (let index = 0; index < this._items.length; index++) {
				const item = this._items[index];

				if (data && data.id === item.source_id && data.source === item.source) {
					findMarker(item);
					break;
				} else if (data && data.source === "coor" && (item.type === "point" || item.type === "entity")) {
					const id = `${item.mark.lon},${item.mark.lat}`;

					if (id === data.id) {
						findMarker(item);
						break;
					}
				}
			}
		} else if (this._activeDetail) {
			this._activeDetail = null;
			this._markerLayer.removeAll();
			this._markerLayer.addMarker(this._allMarkers);
		}
	}

	highlight(item?: any) {
		if (this._deactiveItems || this._openingItem) {
			return;
		}

		if (this._addedMarker) {
			this._markerLayer.removeMarker(this._addedMarker);
			this._addedMarker = null;
		}

		const inMap = this._highlightMarkers(item);

		this._highlightGeometry(geometry => (item && geometry.id === item.id) || !item, !!item);

		if (item && !inMap && item.id !== "homework") {
			this._addedMarker = this._createMarker(item);
			this._markerLayer.addMarker(this._addedMarker);
		}

		this._highlightItem = item || null;
	}

	lookup() {
		const viewport = Scene.getViewport();
		const { zoom } = store.getState().map;
		const beFilter = this._onGetData();

		Mapy.ProviderMapyBox.lookup(this._folderId, [[viewport.lbx, viewport.lby], [viewport.rtx, viewport.rty]], {
			zoom,
			// eslint-disable-next-line camelcase
			...beFilter
				? {
					filters: beFilter,
				}
				: {},
		}).then(data => {
			if (this._items.length) {
				Scene.removeCard();

				this._items.forEach(item => {
					if (item.markerGeometryCard?.appended) {
						this._markerLayer.removeMarker(item.markerGeometryCard);
					}
				});
			}

			this._items = data.items;

			this._geomLayer.removeAll();

			if (this._addedMarker) {
				this._markerLayer.removeMarker(this._addedMarker);
				this._addedMarker = null;
			}

			if (this._highlightItem?.markerGeometryCard) {
				this._showGeometryCard(this._highlightItem);
			}

			this._highlightItem = null;

			if (this._items.length) {
				let newMarkers = [];
				let oldMarkers = [];
				const selectOldMarkers = (id:string): Array<SceneMarkerWindy | SceneMarkerMapyWindyPlace> => {
					const selectedOldMarkers = [];

					this._allMarkers.forEach((marker, key) => {
						if (marker.getId()?.toString().includes(id)) {
							selectedOldMarkers.push(key);
						}
					});

					return selectedOldMarkers;
				};

				this._items.forEach(item => {
					let isOk = false;
					let create = true;

					for (let indx = 0; indx < this._allMarkers.length; indx++) {
						if (this._allMarkers[indx].getId() === item.id) {
							create = false;
							break;
						}
					}

					const showLabel = !!(CONFIG.inFrame && !Scene.isM3dActive() && !Scene.isWasmActive() && ((this._showLabel === 1 && item.usertitle) || (this._showLabel === 2 && (item.usertitle || item.title))));

					if (create) {
						// pouze nektere typy
						switch (item.type) {
							case "route":
								if (item?.data?.geometry) {
									let markers = [];

									if (!item.markers) {
										markers = this._createMapRouteItems(item, showLabel);
									}

									if (this._hideId === "" || item.id !== this._hideId) {
										newMarkers = newMarkers.concat(markers);
									}

									oldMarkers = oldMarkers.concat(selectOldMarkers(item.id));

									this._allMarkers = this._allMarkers.concat(markers);

									isOk = false;
								}
								break;

							case "measure":
								if (item?.data?.points) {
									let markers = [];

									if (!item.markers) {
										markers = this._createMapMeasureItems(item, showLabel);
									}

									if (this._hideId === "" || item.id !== this._hideId) {
										newMarkers = newMarkers.concat(markers);
									}

									oldMarkers = oldMarkers.concat(selectOldMarkers(item.id));

									this._allMarkers = this._allMarkers.concat(markers);

									isOk = false;
								}
								break;

							case "link":
							case "path":
								if (((item.type === "link" && item.linktype === "track") || item.type === "path") && item?.data?.data?.length) {
									let markers = [];

									if (!item.markers) {
										markers = this._createMapPathItems(item, showLabel);
									}

									if (this._hideId === "" || item.id !== this._hideId) {
										newMarkers = newMarkers.concat(markers);
									}

									oldMarkers = oldMarkers.concat(selectOldMarkers(item.id));

									this._allMarkers = this._allMarkers.concat(markers);

									isOk = false;
								}
								break;

							case "entity":
							case "point":
							case "set":
							default:
								isOk = !!item.mark;
								break;
						}

						if (isOk) {
							const marker = this._createMarker(item);

							this._allMarkers.push(marker);

							if (this._activeDetail && marker.getId() === this._activeDetail.item.id) {
								this._activeDetail.marker = marker;
							} else if (this._hideId === "" || item.id !== this._hideId) {
								newMarkers.push(marker);
							}
						}
					}
				});

				for (let index = 0; index < this._allMarkers.length; index++) {
					const marker = this._allMarkers[index];
					let old = true;

					for (let itemsIndex = 0; itemsIndex < this._items.length; itemsIndex++) {
						if (marker.getId()?.toString().includes(this._items[itemsIndex].id)) {
							old = false;
							break;
						}
					}

					if (old) {
						oldMarkers.push(index);
					}
				}

				this._markerLayer.addMarker(newMarkers, true);
				// srovnat, pac geometrie, ktere se delaji jako prvni, muzou mit vetsi indexy
				// tak aby se nestalo ze pri prevraceni se zahazuje neco co nechceme
				oldMarkers.sort((first, second) => first - second);

				const removeMarkers = [];

				oldMarkers.reverse().forEach(item => {
					const marker = this._allMarkers.splice(item, 1);

					if (marker[0].appended) {
						removeMarkers.push(marker[0]);
					}
				});

				if (removeMarkers.length) {
					this._markerLayer.removeMarker(removeMarkers, true);
				}

				this._markerLayer.redraw();
			}
		}, () => {});
	}

	_createMapRouteItems(item, showLabel: boolean): Array<SceneMarkerWindy | SceneMarkerMapyWindyPlace> {
		const routes = item.data.route;
		const markers = [];
		const imageData = getImageData(item);

		markers.push(this._createGeometryMarker(Scene.stringToCoords(routes[routes.length-1].geometry)[0], item, 1, null));
		markers.push(this._createGeometryMarker(Scene.stringToCoords(routes[0].geometry)[0], item, 0, createSVGIcon(imageData), imageData.svgName));

		const geometryCoords = Scene.stringToCoords(item.data.geometry);

		if (showLabel) {
			markers.push(createGeometryLabelMarker(geometryCoords[Math.round(geometryCoords.length/2)], item.usertitle || item.title, item.id));
		} else if (!Scene.isM3dActive() && !CONFIG.inFrame && !item.markerGeometryCard) {
			const { marker, geometry, ...itemRest } = item;

			item.markerGeometryCard = createGeometryCardMarker(JSON.parse(JSON.stringify(itemRest)), item.id, () => {
				this._owner.highlightItem();
				this._owner.highlightMarker();
				this._openDetail(item);
			});
		}

		item.geometry = this._buildGeometry(geometryCoords, item);

		return markers;
	}

	_createMapMeasureItems(item, showLabel: boolean): Array<SceneMarkerWindy | SceneMarkerMapyWindyPlace> {
		const geometryCoords = Scene.stringToCoords(item.data.points);
		const markers = [];
		const imageData = getImageData(item);

		markers.push(this._createGeometryMarker(geometryCoords[geometryCoords.length-1], item, 1));
		markers.push(this._createGeometryMarker(geometryCoords[0], item, 0, createSVGIcon(imageData), imageData.svgName));

		if (showLabel) {
			markers.push(createGeometryLabelMarker(geometryCoords[Math.round(geometryCoords.length/2)], item.usertitle || item.title, item.id));
		} else if (!Scene.isM3dActive() && !CONFIG.inFrame && !item.markerGeometryCard) {
			const { marker, geometry, ...itemRest } = item;

			item.markerGeometryCard = createGeometryCardMarker(JSON.parse(JSON.stringify(itemRest)), item.id, () => {
				this._owner.highlightItem();
				this._owner.highlightMarker();
				this._openDetail(item);
			});
		}

		item.geometry = this._buildGeometry(geometryCoords, item);

		return markers;
	}

	_createMapPathItems(item, showLabel: boolean): Array<SceneMarkerWindy | SceneMarkerMapyWindyPlace> {
		const coords = [];
		let allCoords = [];
		const markers = [];
		const imageData = getImageData(item);

		item.data.data.forEach(part => {
			const partCoords = Scene.stringToCoords(part.route);

			allCoords = allCoords.concat(partCoords);
			coords.push(partCoords);
		});

		markers.push(this._createGeometryMarker(allCoords[allCoords.length-1], item, 1, null));
		markers.push(this._createGeometryMarker(allCoords[0], item, 0, createSVGIcon(imageData), imageData.svgName));

		if (showLabel) {
			markers.push(createGeometryLabelMarker(allCoords[Math.round(allCoords.length/2)], item.usertitle || item.title, item.id));
		} else if (!Scene.isM3dActive() && !CONFIG.inFrame && !item.markerGeometryCard) {
			const { marker, geometry, ...itemRest } = item;

			item.markerGeometryCard = createGeometryCardMarker(JSON.parse(JSON.stringify(itemRest)), item.id, () => {
				this._owner.highlightItem();
				this._owner.highlightMarker();
				this._openDetail(item);
			});
		}

		item.geometry = this._buildGeometry(coords, item);

		return markers;
	}

	_createMarker(item, added?: boolean): SceneMarkerMapyWindyPlace {
		if (item.marker) {
			return item.marker;
		}

		const coords = Scene.coordsFromWGS84(item.mark.lon, item.mark.lat);
		const showLabel = !!(CONFIG.inFrame && !Scene.isM3dActive() && !Scene.isWasmActive() && ((this._showLabel === 1 && item.usertitle) || (this._showLabel === 2 && (item.usertitle || item.title))));
		let markerType = showLabel ? Scene.VISUAL.MARKER_WINDY.LITTLE : Scene.VISUAL.MARKER_WINDY.SMALL;

		// pokud je jen jeden item ve slozce a neni zapnute zobrazeni titulku, tak bude velky marker
		if (CONFIG.inFrame && !showLabel && this._items.length === 1) {
			markerType = Scene.VISUAL.MARKER_WINDY.BIG;
		}

		const opts: IMarkerOptions = {
			type: markerType,
			m3dData: {
				type: "places",
				click: () => {
					this._owner.highlightItem();
					this.highlight();
					this._openDetail(item);
				},
				enter: () => {
					if (this._activeDetail || this._deactiveItems) {
						return;
					}

					this._owner.highlightItem(item, true);
					this.highlight(item);
				},
				leave: () => {
					if (this._activeDetail || this._deactiveItems) {
						return;
					}

					this._owner.highlightItem();
					this.highlight();
				},
			},
		};

		const imageData = getImageData(item);

		if (imageData.img && !showLabel) {
			const typeId = item.poi_type_id;
			const markerIcon = getIcon(typeId, { scale: 1 });
			let srcset = "";

			if (markerIcon) {
				const icon2x = getIcon(typeId, { scale: 2 });

				if (icon2x) {
					srcset = `${markerIcon} 1x ${icon2x ? `, ${icon2x} 2x` : ""}`;
				}
			}

			opts.icon = markerIcon || undefined;
			opts.srcset = srcset || undefined;
		}

		if (!opts.icon && !showLabel && !added) {
			const svgIcon = createSVGIcon(imageData);

			opts.elm = svgIcon;
			opts.m3dData.svg = imageData.svgName;
		}

		opts.cardClick = () => {
			this._openDetail(item);
		};

		const marker = item.source === "wcam"
			? Scene.createMarkerWindyWebcamFromData(item)
			: Scene.createMarkerMapyWindyPlace(coords, JSON.parse(JSON.stringify(item)), opts);

		if (!added) {
			marker.addEventListener("click", () => {
				this._owner.highlightItem();
				this._owner.highlightMarker();
				this._openDetail(item);
			});
			marker.addEventListener("pointerenter", () => {
				if (this._activeDetail || this._deactiveItems) {
					return;
				}

				this._owner.highlightItem(item, true);
				this._owner.highlightMarker({ data: item });
			});
			marker.addEventListener("pointerleave", () => {
				if (this._activeDetail || this._deactiveItems) {
					return;
				}

				this._owner.highlightItem();
				this._owner.highlightMarker();
			});

			if (showLabel) {
				marker.setLabel(item.usertitle || item.title);
			}

			item.marker = marker;
		}

		if (this._deactiveItems) {
			marker.setActive();
			"setInteractive" in marker && marker.setInteractive(true);
		}

		return marker;
	}

	_buildGeometry(geometryCoords: Array<SceneCoords>, item): SceneGeometrySmart {
		const geometry = Scene.createGeometrySmart(Scene.GEOMETRY.POLYLINE, null, null, {
			color: this._deactiveItems || (this._highlightItem && this._highlightItem !== item) ? GEOM_GRAY_COLOR : GEOM_RED_COLOR,
			width: 4,
			outlineColor: "#fff",
			outlineWidth: 1,
			outlineOpacity: 0.1,
		});

		// pri zmene smart geometrie -> rebind
		geometry.getAllNodes(nodes => {
			nodes.forEach(elm => {
				// spravny kurzor
				elm.style.cursor = "pointer";

				elm.addEventListener("click", () => {
					Scene.removeCard();
					this._openDetail(item);
				});
				elm.addEventListener("pointerenter", event => {
					this._owner.highlightItem(item, true);
					this._owner.highlightMarker(item, true);
					this.highlight(item);

					this._showGeometryCard(event, item, true);
				});
				elm.addEventListener("pointerleave", event => {
					this._showGeometryCard(event, item);

					this._owner.highlightItem();
					this._owner.highlightMarker();
					this.highlight();
				});
				elm.addEventListener("pointermove", event => {
					this._showGeometryCard(event, item, true);
				});
			});
		});

		if (this._hideId === "" || item.id !== this._hideId) {
			this._geomLayer.addSmartGeometry(geometry);
		}
		geometry.setCoords(geometryCoords);

		if (!this._highlightGeometryItem) {
			this._highlightGeometryItem = Scene.createGeometrySmart(Scene.GEOMETRY.POLYLINE, null, null, {
				color: GEOM_RED_COLOR,
				width: 4,
				outlineWidth: 0,
			});

			this._highlightGeometryItem.getAllNodes(nodes => {
				nodes.forEach(elm => {
					elm.style.pointerEvents = "none";
				});
			});
		}

		return geometry;
	}

	_createGeometryMarker(coord: SceneCoords, item: any, key: number, iconElement?: HTMLSpanElement, svgName?: string) {
		const { marker: markerOut, geometry, markerGeometryCard, ...restItem } = item;

		const marker = Scene.createMarkerMapyWindyPlace(coord, JSON.parse(JSON.stringify(restItem)), {
			type: iconElement ? Scene.VISUAL.MARKER_WINDY.SMALL : Scene.VISUAL.MARKER_WINDY.LITTLE,
			m3dData: {
				type: "placesRoute",
				click: () => {
					this._openDetail(item);
				},
				enter: () => {
					if (this._activeDetail || this._deactiveItems) {
						return;
					}

					this._owner.highlightItem(item, true);
					this.highlight(item);
				},
				leave: () => {
					if (this._activeDetail || this._deactiveItems) {
						return;
					}

					this._owner.highlightItem();
					this.highlight();
				},
				...svgName ? { svg: svgName } : {},
			},
			cardClick: () => {
				this._owner.highlightItem();
				this._owner.highlightMarker();
				this._openDetail(item);
			},
			...iconElement ? { elm: iconElement } : {},
		}, `${item.id}-${key}-${Date.now()}`);

		marker.addEventListener("click", () => {
			this._openDetail(item);
		});
		marker.addEventListener("pointerenter", () => {
			if (this._activeDetail || this._deactiveItems) {
				return;
			}

			this._owner.highlightItem(item, true);
			this._owner.highlightMarker({ data: item });
			this.highlight(item);
		});
		marker.addEventListener("pointerleave", () => {
			if (this._activeDetail || this._deactiveItems) {
				return;
			}

			this._owner.highlightItem();
			this._owner.highlightMarker();
			this.highlight();
		});

		if (this._deactiveItems || (this._highlightItem && this._highlightItem !== item)) {
			marker.setActive();
			"setInteractive" in marker && marker.setInteractive(true);
		}

		return marker;
	}

	/**
	 * Data z requestu pro detail liku.
	 *
	 * @param  {Object} data Like.detail
	 */
	_openDetail(data, replace = false) {
		const History = Mapy.getComponent("history");

		if (CONFIG.inFrame) {
			const state = History.state;

			iframeAnalyticsLog({
				type: "click-back",
				referrer: document.referrer,
				form: "other",
			});

			window.open(`${CONFIG.mapyURL}/${state.mapset}?dim=${data.id}`);
		} else {
			const Dimcurator = Mapy.getComponent("dimcurator");
			const myMaps = Mapy.getComponent("mymaps");
			const { longitude, latitude, zoom } = store.getState().map;
			const actualDIMState = Dimcurator.getActualItemState();

			this._openingItem = data;

			if (actualDIMState?.backAction) {
				actualDIMState.backAction();
			}

			// pri replace url chceme smazat data pro prechod zpet
			if (replace) {
				Mapy.getComponent("places").state = {};
			}

			Dimcurator.setState({
				dim: data.id,
				backTitle: _("back"),
				backAction: event => {
					if (data.parent_id && event) {
						Mapy.getComponent("places").state = { mid: data.parent_id };
					}

					switch (data.type) {
						case "path":
						case "measure":
							Mapy.getComponent("path").deactivate();
							break;

						case "entity":
						case "point":
							store.dispatch(unsetDetail());
							event && store.dispatch(setCoordinates({
								longitude,
								latitude,
								zoom,
								animate: false,
							}));
							break;

						case "link":
							if (data.linktype === "track") {
								Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("places"), true);
							}
							break;
						default:
							break;
					}

					if (event) {
						Mapy.getComponent("dimcurator").setState();
						Mapy.getComponent("history").save();
					} else {
						Mapy.getComponent("userMarks").isActive() && Mapy.getComponent("userMarks").deactivate();
						Mapy.getComponent("distanceMeter").isActive() && Mapy.getComponent("distanceMeter").deactivate();
					}
				},
			}).loadData().then(() => {
				Scene.removeCard();

				this._openingItem = null;
				this.hideMarker(data);
				History.save(replace);
			}, () => {
				Scene.removeCard();
				this._highlightMarkers();
				this._highlightGeometry(() => true);
			});

			myMaps.disableZoomToItems();
		}
	}

	_highlightMarkers(item?: any): boolean {
		let inMap = false;

		for (let index = 0; index < this._allMarkers.length; index++) {
			const marker = this._allMarkers[index];

			if (marker instanceof WindyWebcamMarker) {
				const isActive = item && marker.getId().split("-")[0] === item.id;

				marker.setActive(isActive);

				if (item && isActive) {
					inMap = true;
				}
			} else if ((item && marker.getId().split("-")[0] === item.id) || (!item && !this._openingItem)) {
				marker.setActive(true);
				"setInteractive" in marker && marker.setInteractive();

				if (item) {
					inMap = true;
				}
			} else {
				marker.setActive();
				"setInteractive" in marker && marker.setInteractive(true);
			}
		}

		return inMap;
	}

	_highlightGeometry(highlightCb: (item?: any) => boolean, hover?: boolean) {
		const route = this._items.filter(item => !!item.geometry);

		route.forEach(item => {
			if (item.geometry) {
				if (hover && highlightCb(item) && this._highlightGeometryItem) {
					this._highlightGeometryItem.setCoords(item.geometry.getCoords());
					this._geomLayer.addSmartGeometry(this._highlightGeometryItem);
					this._highlightGeometryItem.rebind();
				} else if (!hover && this._highlightGeometryItem?.getCoords()?.length) {
					this._highlightGeometryItem.setCoords([]);
					this._highlightGeometryItem.appended && this._geomLayer.removeSmartGeometry(this._highlightGeometryItem);
				}

				item.geometry.setOptions({
					color: highlightCb(item) ? hover ? "rgba(255,255,255,0.1)" : GEOM_RED_COLOR : GEOM_GRAY_COLOR,
				});
			}
		});
	}

	_showGeometryCard(event, item = null, show = false) {
		if (item?.markerGeometryCard) {
			if (show) {
				const pixel = Scene.pixelFromEvent(event);
				const coords = Scene.pixelToCoords(pixel);

				item.markerGeometryCard.setCoords(coords);

				if (item.markerGeometryCard.appended) {
					item.markerGeometryCard.moveCard();
				} else {
					this._markerLayer.addMarker(item.markerGeometryCard);
					item.markerGeometryCard.showCard();
				}
			} else {
				Scene.removeCard();
				if (item.markerGeometryCard.appended) {
					this._markerLayer.removeMarker(item.markerGeometryCard);
				}
			}
		}
	}
}
